import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import Loading from '@/widgets/Loading'
import AvailabilityService from '@/services/AvailabilityService'

const formatDay = value => {
	const date = moment(value)
	return date.isValid() ? date.format('DD.MM.YYYY') : '—'
}

const formatSlots = (morning, afternoon) => {
	if (morning && afternoon) return '8-20'
	if (morning) return '8-14'
	if (afternoon) return '14-20'
	return '—'
}

export default function ZmianyDostepnosciPage() {
	const { t } = useTranslation()
	const [requests, setRequests] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [pendingId, setPendingId] = useState('')

	const load = useCallback(async () => {
		setLoading(true)
		setError('')
		const result = await AvailabilityService.getRequests()
		setRequests(result.data || [])
		setError(result.error || '')
		setLoading(false)
	}, [])

	useEffect(() => {
		load()
	}, [load])

	const handleDecision = async (requestId, action) => {
		if (!requestId || pendingId) return
		setPendingId(requestId)
		setError('')
		const result = action === 'accept'
			? await AvailabilityService.acceptRequest(requestId)
			: await AvailabilityService.rejectRequest(requestId)
		setPendingId('')
		if (!result.success) {
			setError(result.error || t('availabilityChangeRequests.actionFailed'))
			return
		}
		setRequests(prev => prev.filter(item => String(item._id) !== String(requestId)))
	}

	if (loading) return <Loading />

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-semibold text-gray-900">{t('availabilityChangeRequests.title')}</h1>
				<p className="mt-1 text-sm text-gray-500">{t('availabilityChangeRequests.description')}</p>
			</div>

			{error ? (
				<div className="rounded-md bg-yellow-50 p-4 text-sm text-yellow-800">{error}</div>
			) : null}

			<div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
				<table className="min-w-full divide-y divide-gray-200 text-sm">
					<thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
						<tr>
							<th className="px-4 py-3">{t('availabilityChangeRequests.columns.trainer')}</th>
							<th className="px-4 py-3">{t('availabilityChangeRequests.columns.type')}</th>
							<th className="px-4 py-3">{t('availabilityChangeRequests.columns.current')}</th>
							<th className="px-4 py-3">{t('availabilityChangeRequests.columns.proposed')}</th>
							<th className="px-4 py-3">{t('availabilityChangeRequests.columns.actions')}</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{requests.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-4 py-8 text-center text-gray-500">
									{t('availabilityChangeRequests.empty')}
								</td>
							</tr>
						) : requests.map(request => {
							const isEdit = request.type === 'edit'
							const busy = pendingId === String(request._id)
							return (
								<tr key={request._id}>
									<td className="px-4 py-3 font-medium text-gray-900">{request.userName || '—'}</td>
									<td className="px-4 py-3 text-gray-700">
										{isEdit
											? t('availabilityChangeRequests.types.edit')
											: t('availabilityChangeRequests.types.delete')}
									</td>
									<td className="px-4 py-3 text-gray-700">
										<div>{formatDay(request.currentStartDate)}</div>
										<div className="text-xs text-gray-500">
											{formatSlots(request.currentMorning, request.currentAfternoon)}
										</div>
									</td>
									<td className="px-4 py-3 text-gray-700">
										{isEdit ? (
											<>
												<div>{formatDay(request.startDate)}</div>
												<div className="text-xs text-gray-500">
													{formatSlots(request.morning, request.afternoon)}
												</div>
												{request.notes ? (
													<div className="mt-1 text-xs text-gray-500">{request.notes}</div>
												) : null}
											</>
										) : (
											<span className="text-gray-400">—</span>
										)}
									</td>
									<td className="px-4 py-3">
										<div className="flex flex-wrap gap-2">
											<button
												type="button"
												disabled={busy}
												onClick={() => handleDecision(request._id, 'accept')}
												className={`rounded-md px-3 py-1.5 text-xs font-semibold text-white ${
													busy ? 'cursor-not-allowed bg-emerald-300' : 'bg-emerald-600 hover:bg-emerald-500'
												}`}>
												{t('availabilityChangeRequests.actions.accept')}
											</button>
											<button
												type="button"
												disabled={busy}
												onClick={() => handleDecision(request._id, 'reject')}
												className={`rounded-md px-3 py-1.5 text-xs font-semibold text-white ${
													busy ? 'cursor-not-allowed bg-red-300' : 'bg-red-600 hover:bg-red-500'
												}`}>
												{t('availabilityChangeRequests.actions.reject')}
											</button>
										</div>
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}
